'use client'
import { type FC, useMemo } from 'react'
import { Box, type StackProps, Text, VStack } from '@chakra-ui/react'
import { z } from 'zod'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import {
  FormProvider,
  GrandFormInputField,
  GrandFormSubmitButton,
  type Resolver,
  useForm,
  zodResolver
} from '../grand-form'

export interface LoginFormType {
  email: string
  password: string
}

export interface LoginFormOverrides {
  loginFormEmail?: string
  loginFormPwd?: string
  loginFormBtnLabel?: string
  loginFormBtnLoading?: string
  loginFormEmailInvalid?: string
  loginFormPwdRequired?: string
}

interface LoginFormProps extends Omit<StackProps, 'onSubmit'> {
  onSubmit: (data: LoginFormType) => Promise<void> | void
  error?: string
  defaultValues?: Partial<LoginFormType>
}

const LoginForm: FC<LoginFormProps> = ({ onSubmit, error, defaultValues, ...stackProps }) => {
  const overrides = useOverridesContext<keyof LoginFormOverrides>()
  const emailLabel = overrideText('E-postadress', overrides?.loginFormEmail)
  const passwordLabel = overrideText('Lösenord', overrides?.loginFormPwd)
  const btnLabel = overrideText('Logga in', overrides?.loginFormBtnLabel)
  const btnLoading = overrideText('Loggar in…', overrides?.loginFormBtnLoading)
  const emailInvalidMsg = overrideText(
    'Ange en giltig e-postadress.',
    overrides?.loginFormEmailInvalid
  )
  const pwdRequiredMsg = overrideText('Lösenord krävs.', overrides?.loginFormPwdRequired)

  const schema = useMemo(
    () =>
      z.object({
        email: z.email(emailInvalidMsg),
        password: z.string().min(1, pwdRequiredMsg)
      }),
    [emailInvalidMsg, pwdRequiredMsg]
  )

  const methods = useForm<LoginFormType>({
    defaultValues: { email: '', password: '', ...defaultValues },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema) as Resolver<LoginFormType>
  })

  return (
    <FormProvider {...methods}>
      <Box as="form" onSubmit={methods.handleSubmit(onSubmit)} w="full">
        <VStack align="stretch" spacing={4} w="full" {...stackProps}>
          <GrandFormInputField
            control={methods.control}
            label={emailLabel}
            name="email"
            placeholder={emailLabel}
            required
            type="email"
          />
          <GrandFormInputField
            control={methods.control}
            label={passwordLabel}
            name="password"
            placeholder={passwordLabel}
            required
            type="password"
          />
          {error ? (
            <Text color="status.error" fontSize="sm">
              {error}
            </Text>
          ) : null}
          <GrandFormSubmitButton disableIfInvalid={false} loadingText={btnLoading}>
            {btnLabel}
          </GrandFormSubmitButton>
        </VStack>
      </Box>
    </FormProvider>
  )
}

export default LoginForm
